<script setup lang="ts">
import { onMounted, ref } from 'vue'
import LoadingSpinner from '~/components/common/LoadingSpinner.vue'
import { showcaseCvRow } from '~/constants/showcaseCvs'
import { useCVSectionTranslations } from '~/composables/useCVSectionTranslations'
// Get the current page props/data
const { t } = useI18n()
const route = useRoute()
const user = useSupabaseUser()
const supabase = useSupabaseClient()
const { resolvePublicCvId } = usePublicCvId()

// The row id behind the URL, which may carry the CV's public slug instead
const cvId = ref<string>('')

// Mobile menu state
const mobileMenuOpen = ref(false)

// User data from CV
const userName = ref<string>('')
const userTitle = ref<string>('')
const userPhoto = ref<string>('')
const userPosition = ref<string>('')
const activeSection = ref('about')
const cvOwnerId = ref<string>('')
const isPublic = ref<boolean>(false)
const isLoading = ref<boolean>(true)
const cvLanguage = ref<string>('en')
// Admin panel ref
const adminPanelRef = ref()

// Get CV section translations based on cvLanguage
const { translations: sectionTranslations } = useCVSectionTranslations(cvLanguage)

// Check if current user is the owner
const isOwner = computed(() => {
  return user.value?.id === cvOwnerId.value
})

// Toggle public visibility - just send signal to page component
const handleTogglePublic = () => {
  // Send signal to page component to execute togglePublicStatus
  if (import.meta.client) {
    window.dispatchEvent(new CustomEvent('toggle-public-status-request'))
  }
}

// Change template
const handleChangeTemplate = async (template: string) => {
  try {
    if (!cvId.value) return
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error } = await (supabase as any)
      .from('cv_requests')
      .update({ business_web_template: template })
      .eq('id', cvId.value)

    if (!error) {
      // Emit event to update template in page component
      if (import.meta.client) {
        window.dispatchEvent(new CustomEvent('template-changed', { detail: { template } }))
      }
    } else {
      console.error('Error updating template:', error)
    }
  } catch (e) {
    console.error('Error changing template:', e)
  }
}

const scrollToSection = (section: string) => {
  activeSection.value = section
  const element = document.getElementById(section)
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

// Download contact function
const downloadContact = () => {
  if (!userName.value) return

  // Create vCard format
  const vcard = [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `FN:${userName.value}`,
    userTitle.value ? `TITLE:${userTitle.value}` : '',
    'END:VCARD'
  ].filter(line => line && line !== '').join('\n')

  // Create download
  const blob = new Blob([vcard], { type: 'text/vcard' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `${userName.value.replace(/\s+/g, '_')}.vcf`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

// Load CV data to get user name
onMounted(async () => {
  cvId.value = await resolvePublicCvId() ?? ''
  if (cvId.value) {
    try {
      isLoading.value = true
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: row } = await (supabase as any)
        .from('cv_requests')
        .select('parsed_data, user_id, is_public, business_web_template')
        .eq('id', cvId.value)
        .single()
      // A gallery example whose row has not been created yet: the demo sample
      const data = row ?? showcaseCvRow(cvId.value)
      if (data) {
        const parsedData = data.parsed_data as { name?: string; title?: string; photoUrl?: string; cvLanguage?: string } | null
        userName.value = parsedData?.name || ''
        userTitle.value = parsedData?.title || ''
        userPhoto.value = parsedData?.photoUrl || ''
        userPosition.value = parsedData?.title || ''
        cvOwnerId.value = data.user_id || ''
        isPublic.value = data.is_public || false
        cvLanguage.value = parsedData?.cvLanguage || 'en'
      }
    } catch (e) {
      console.error('Error loading CV data for layout:', e)
    } finally {
      isLoading.value = false
    }
  } else {
    isLoading.value = false
  }

  // Listen for public status changes from page component
  if (import.meta.client) {
    window.addEventListener('public-status-changed-from-page', (event: Event) => {
      const customEvent = event as CustomEvent<{ isPublic: boolean }>
      isPublic.value = customEvent.detail.isPublic
      // Update admin panel state
      if (adminPanelRef.value) {
        adminPanelRef.value.setPublicStatus(customEvent.detail.isPublic)
        adminPanelRef.value.setTogglingState(false)
      }
    })

    // Listen for toggle completion to reset loading state
    window.addEventListener('toggle-public-status-complete', () => {
      if (adminPanelRef.value) {
        adminPanelRef.value.setTogglingState(false)
      }
    })
  }
})
</script>

<template>
  <div class="flex min-h-screen flex-col bg-gray-50 dark:bg-gray-900">
    <!-- Admin Navigation Bar (Only for Owner) -->
    <CommonAdminPanel
v-if="isOwner" ref="adminPanelRef" :cv-id="cvId"
      :show-template-selector="true" :show-public-toggle="true" :show-edit-button="true" :show-my-cvs-button="true"
      @toggle-public="handleTogglePublic" @change-template="handleChangeTemplate" />

    <!-- Modern Header with User Info -->
    <nav
      class="fixed z-50 w-full border-b border-gray-100 bg-white/95 backdrop-blur-lg dark:border-gray-800 dark:bg-gray-900/95"
      :class="isOwner ? 'top-14' : 'top-0'">
      <div class="mx-auto max-w-screen-xl px-4">
        <!-- Public CV or Owner View - Full Navigation -->
        <div v-if="isPublic || isOwner" class="flex h-14 items-center justify-between sm:h-16">
          <!-- Logo/Name -->
          <div class="flex items-center space-x-2 sm:space-x-3">
            <div class="flex size-8 items-center justify-center rounded-lg bg-indigo-600 sm:size-10 sm:rounded-xl">
              <img v-if="userPhoto" :src="userPhoto" :alt="userName" class="size-full rounded-xl object-cover">

              <div v-else class="text-sm font-bold text-white sm:text-lg">{{ userName?.charAt(0)}}</div>

            </div>
            <div class="flex flex-col">
              <div
                class="max-w-[120px] truncate text-sm font-semibold text-gray-900 sm:max-w-none sm:text-base dark:text-white">
                {{ userName }}</div>
              <div v-if="userPosition" class="text-xs text-gray-600 dark:text-gray-400">{{ userPosition }}</div>
            </div>
            <div class="mx-2 hidden h-6 w-px bg-gray-300 sm:block dark:bg-gray-600" />

            <!-- InMyResume Logo (smaller) -->
            <div class="hidden items-center space-x-1 opacity-70 transition-opacity hover:opacity-100 sm:flex">
              <LocaleLink to="/" class="flex items-center">
                <NuxtImg src="/logo.svg" alt="InMyResume" width="264" height="56" class="h-7 w-auto dark:hidden" />
                <NuxtImg
src="/logo_white.svg" alt="InMyResume" width="264" height="56"
                  class="hidden h-7 w-auto dark:block" />
              </LocaleLink>
            </div>
          </div>

          <!-- Mobile Menu Button -->
          <button
            class="rounded-lg p-2 text-gray-600 hover:bg-gray-100 md:hidden dark:text-gray-300 dark:hover:bg-gray-800"
            @click="mobileMenuOpen = !mobileMenuOpen">
            <svg class="size-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
v-if="!mobileMenuOpen" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M4 6h16M4 12h16M4 18h16" />
              <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <!-- Desktop Navigation -->
          <div class="hidden items-center space-x-6 md:flex lg:space-x-8">
            <button
              :class="activeSection === 'about' ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-600 dark:text-gray-300'"
              class="text-sm font-medium transition-colors hover:text-indigo-600 lg:text-base dark:hover:text-indigo-400"
              @click="scrollToSection('about')">
              {{ sectionTranslations.aboutMe }}
            </button>
            <button
              :class="activeSection === 'experience' ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-600 dark:text-gray-300'"
              class="text-sm font-medium transition-colors hover:text-indigo-600 lg:text-base dark:hover:text-indigo-400"
              @click="scrollToSection('experience')">
              {{ sectionTranslations.experiences }}
            </button>
            <button
              :class="activeSection === 'skills' ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-600 dark:text-gray-300'"
              class="text-sm font-medium transition-colors hover:text-indigo-600 lg:text-base dark:hover:text-indigo-400"
              @click="scrollToSection('skills')">
              {{ sectionTranslations.skills }}
            </button>
            <button
              :class="activeSection === 'contact' ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-600 dark:text-gray-300'"
              class="text-sm font-medium transition-colors hover:text-indigo-600 lg:text-base dark:hover:text-indigo-400"
              @click="scrollToSection('contact')">
              {{ sectionTranslations.contact }}
            </button>
          </div>

          <!-- Desktop CTA Button -->
          <button
            class="hidden rounded-full bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition-all hover:scale-105 hover:bg-indigo-700 md:inline-flex lg:px-6"
            @click="downloadContact">
            {{ sectionTranslations.saveContact }}
          </button>
        </div>

        <!-- Private CV - Simple Navigation with Logo and Auth Buttons -->
        <div v-else class="flex h-14 items-center justify-between sm:h-16">
          <!-- InMyResume Logo -->
          <LocaleLink to="/" class="flex items-center">
            <NuxtImg src="/logo.svg" alt="InMyResume" width="264" height="56" class="h-9 w-auto dark:hidden" />
            <NuxtImg
src="/logo_white.svg" alt="InMyResume" width="264" height="56"
              class="hidden h-9 w-auto dark:block" />
          </LocaleLink>

          <!-- Auth Buttons -->
          <div class="flex items-center space-x-3">
            <LocaleLink
to="/login"
              class="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800">
              {{ t('publicPage.signIn') }}
            </LocaleLink>
            <LocaleLink
to="/registration"
              class="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-700">
              {{ t('publicPage.signUp') }}
            </LocaleLink>
          </div>
        </div>

        <!-- Mobile Menu (only for public/owner view) -->
        <div
v-if="(isPublic || isOwner) && mobileMenuOpen"
          class="border-t border-gray-100 py-4 md:hidden dark:border-gray-800">
          <div class="space-y-2">
            <button
              :class="activeSection === 'about' ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400' : 'text-gray-600 dark:text-gray-300'"
              class="block w-full rounded-lg px-4 py-2 text-left font-medium transition-colors hover:bg-gray-50 dark:hover:bg-gray-800"
              @click="scrollToSection('about'); mobileMenuOpen = false">
              {{ sectionTranslations.aboutMe }}
            </button>
            <button
              :class="activeSection === 'experience' ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400' : 'text-gray-600 dark:text-gray-300'"
              class="block w-full rounded-lg px-4 py-2 text-left font-medium transition-colors hover:bg-gray-50 dark:hover:bg-gray-800"
              @click="scrollToSection('experience'); mobileMenuOpen = false">
              {{ sectionTranslations.experiences }}
            </button>
            <button
              :class="activeSection === 'skills' ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400' : 'text-gray-600 dark:text-gray-300'"
              class="block w-full rounded-lg px-4 py-2 text-left font-medium transition-colors hover:bg-gray-50 dark:hover:bg-gray-800"
              @click="scrollToSection('skills'); mobileMenuOpen = false">
              {{ sectionTranslations.skills }}
            </button>
            <button
              :class="activeSection === 'contact' ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400' : 'text-gray-600 dark:text-gray-300'"
              class="block w-full rounded-lg px-4 py-2 text-left font-medium transition-colors hover:bg-gray-50 dark:hover:bg-gray-800"
              @click="scrollToSection('contact'); mobileMenuOpen = false">
              {{ sectionTranslations.contact }}
            </button>
            <button
              class="mt-3 block w-full rounded-full bg-indigo-600 px-4 py-2 text-center font-medium text-white transition-all hover:bg-indigo-700"
              @click="downloadContact(); mobileMenuOpen = false">
              {{ sectionTranslations.saveContact }}
            </button>
          </div>
        </div>
      </div>
    </nav>
    <nav
v-if="false"
      class="sticky top-0 z-50 border-b border-gray-200 bg-white/95 backdrop-blur-lg dark:border-gray-700 dark:bg-gray-900/95">
      <div class="mx-auto max-w-screen-xl px-4">
        <div class="flex h-14 items-center justify-between">
          <!-- Left: User Name or Logo -->
          <div class="flex items-center space-x-3">
            <LocaleLink to="/" class="group flex items-center space-x-2">
              <!-- User name if available -->
              <div v-if="userName" class="flex items-center space-x-2">
                <div
                  class="flex size-8 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 text-sm font-bold text-white">
                  {{ userName?.charAt(0)?.toUpperCase() || 'U' }}
                </div>
                <div class="hidden sm:block">
                  <div class="text-sm font-semibold text-gray-900 dark:text-white">{{ userName }}</div>
                  <div v-if="userTitle" class="text-xs text-gray-600 dark:text-gray-400">{{ userTitle }}</div>
                </div>
              </div>

              <!-- Separator -->
              <div v-if="userName" class="mx-2 hidden h-6 w-px bg-gray-300 sm:block dark:bg-gray-600" />

              <!-- InMyResume Logo (smaller) -->
              <div class="flex items-center space-x-1 opacity-70 transition-opacity hover:opacity-100">
                <NuxtImg src="/logo.svg" alt="InMyResume" width="264" height="56" class="h-7 w-auto dark:hidden" />
                <NuxtImg
src="/logo_white.svg" alt="InMyResume" width="264" height="56"
                  class="hidden h-7 w-auto dark:block" />
              </div>
            </LocaleLink>
          </div>

          <!-- Right: Actions -->
          <div class="flex items-center space-x-3">
            <!-- Desktop Navigation -->
            <div class="hidden items-center space-x-4 md:flex">
              <!-- Login/Register for non-logged users -->
              <div v-if="!user" class="flex items-center space-x-2">
                <LocaleLink
to="/login"
                  class="px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:text-gray-900 dark:text-gray-300 dark:hover:text-white">
                  {{ t('publicPage.signIn') }}
                </LocaleLink>
                <LocaleLink
to="/register"
                  class="rounded-full bg-indigo-600 px-4 py-1.5 text-sm font-medium text-white transition-colors hover:bg-indigo-700">
                  {{ t('publicPage.signUp') }}
                </LocaleLink>
              </div>

              <!-- User menu for logged-in users -->
              <div v-else class="flex items-center space-x-3">
                <LocaleLink
to="/cv"
                  class="px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:text-gray-900 dark:text-gray-300 dark:hover:text-white">
                  {{ t('nav.cv') }}
                </LocaleLink>
                <LocaleLink
to="/profile"
                  class="px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:text-gray-900 dark:text-gray-300 dark:hover:text-white">
                  {{ t('nav.profile') }}
                </LocaleLink>
              </div>

              <NavbarColorModeSwitcher />
            </div>

            <!-- Mobile menu button -->
            <button
              class="rounded-lg p-2 text-gray-600 transition-colors hover:bg-gray-100 md:hidden dark:text-gray-300 dark:hover:bg-gray-800"
              @click="mobileMenuOpen = !mobileMenuOpen">
              <svg class="size-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
v-if="!mobileMenuOpen" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M4 6h16M4 12h16M4 18h16" />
                <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <!-- Mobile Menu -->
        <div v-if="mobileMenuOpen" class="border-t border-gray-200 py-3 md:hidden dark:border-gray-700">
          <div class="space-y-1">
            <!-- Non-logged user menu -->
            <template v-if="!user">
              <LocaleLink
to="/login"
                class="block rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
                @click="mobileMenuOpen = false">
                {{ t('publicPage.signIn') }}
              </LocaleLink>
              <LocaleLink
to="/register"
                class="block rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
                @click="mobileMenuOpen = false">
                {{ t('publicPage.signUp') }}
              </LocaleLink>
            </template>

            <!-- Logged-in user menu -->
            <template v-else>
              <LocaleLink
to="/cv"
                class="block rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
                @click="mobileMenuOpen = false">
                {{ t('nav.cv') }}
              </LocaleLink>
              <LocaleLink
to="/profile"
                class="block rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
                @click="mobileMenuOpen = false">
                {{ t('nav.profile') }}
              </LocaleLink>
              <button
                class="block w-full rounded-lg px-4 py-2 text-left text-sm font-medium text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                @click="supabase.auth.signOut(); mobileMenuOpen = false">
                {{ t('nav.logout') }}
              </button>
            </template>

            <!-- Theme switcher -->
            <div class="px-4 py-2">
              <NavbarColorModeSwitcher />
            </div>
          </div>
        </div>
      </div>
    </nav>

    <!-- Main content area -->
    <main class="flex flex-1" :class="isOwner ? 'mt-14' : ''">
      <div class="w-full">
        <!-- Show loading spinner if loading -->
        <div v-if="isLoading" class="flex min-h-screen items-center justify-center px-4 ">
          <LoadingSpinner size="lg" class="size-40" />
        </div>
        <!-- Show content if public OR if user is owner -->
        <div v-else-if="(isPublic || isOwner) && !isLoading">
          <slot />
        </div>

        <!-- Show "Not Public" message if not public and user is not owner -->
        <div v-else class="flex min-h-screen items-center justify-center px-4">
          <div class="max-w-md text-center">
            <div class="mb-6 flex justify-center">
              <div class="rounded-full bg-gray-100 p-6 dark:bg-gray-800">
                <svg
class="size-16 text-gray-400 dark:text-gray-600" fill="none" stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
            </div>
            <h1 class="mb-3 text-2xl font-bold text-gray-900 dark:text-white">
              {{ t('publicPage.private.title') }}
            </h1>
            <p class="mb-8 text-gray-600 dark:text-gray-400">
              {{ t('publicPage.private.visitor') }}
            </p>
            <div class="flex flex-col gap-3 sm:flex-row sm:justify-center">
              <LocaleLink
to="/login"
                class="rounded-lg bg-indigo-600 px-6 py-3 font-medium text-white transition-colors hover:bg-indigo-700">
                {{ t('publicPage.signIn') }}
              </LocaleLink>
              <LocaleLink
to="/"
                class="rounded-lg border border-gray-300 bg-white px-6 py-3 font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700">
                {{ t('publicPage.goHome') }}
              </LocaleLink>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- Minimal Footer -->
    <footer class="border-t border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
      <div class="mx-auto max-w-screen-xl px-4 py-6">
        <div class="flex flex-col items-center justify-between space-y-4 sm:flex-row sm:space-y-0">
          <div class="text-sm text-gray-600 dark:text-gray-400">
            © {{ new Date().getFullYear() }} InMyResume. {{ t('footer.rights') }}.
          </div>
          <div class="flex items-center space-x-6">
            <LocaleLink
to="/privacy"
              class="text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white">
              {{ t('footer.privacy') }}
            </LocaleLink>
            <LocaleLink
to="/terms"
              class="text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white">
              {{ t('footer.terms') }}
            </LocaleLink>
            <a
href="mailto:info@inmyresume.com"
              class="text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white">
              {{ t('footer.contact') }}
            </a>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<style>
@media print {
  body {
    background: white !important;
  }

  nav,
  footer,
  .print\:hidden {
    display: none !important;
  }
}
</style>
